import {Component, Input, OnChanges, OnInit, SimpleChanges, ViewChild} from "@angular/core";
import {GridDataResult, PageChangeEvent} from "@progress/kendo-angular-grid";
import {TabStripComponent} from "@progress/kendo-angular-layout";
import * as moment from "moment";
import {EstoqueService} from "../../services/estoque.service";

@Component({
  selector: "app-tela-relatorios-custo-estoque",
  templateUrl: "./tela-relatorios-custo-estoque.component.html",
  styleUrls: ["./tela-relatorios-custo-estoque.component.scss"]
})
export class TelaRelatoriosCustoEstoqueComponent implements OnInit, OnChanges {
  @Input() grupos: any[] = [];
  @Input() insumos: any[] = [];
  @ViewChild("abasRelatorios") abasRelatorios: TabStripComponent;

  posicao: GridDataResult = {data: [], total: 0};
  movimentacoes: GridDataResult = {data: [], total: 0};
  evolucao: any[] = [];
  evolucaoCategorias: string[] = [];
  evolucaoValores: number[] = [];
  inconsistencias: any[] = [];

  resumoPosicao: any = {total: 0, pendenciasCusto: 0, valorTotal: null};
  resumoMovimentacoes: any = {total: 0, custoTotalMovimentado: null};

  carregandoPosicao = false;
  carregandoMovimentacoes = false;
  carregandoEvolucao = false;
  carregandoInconsistencias = false;
  erroPosicao: string;
  erroMovimentacoes: string;
  erroEvolucao: string;
  erroInconsistencias: string;

  skipPosicao = 0;
  tamanhoPosicao = 20;
  skipMovimentacoes = 0;
  tamanhoMovimentacoes = 20;

  filtroPosicao: any = {texto: "", grupo: null};
  filtroMovimentacoes: any = {
    inicio: moment().subtract(30, "days").toDate(),
    fim: new Date(),
    grupo: null,
    insumo: null,
    tipo: null
  };
  filtroEvolucao: any = {
    inicio: moment().subtract(1, "year").toDate(),
    fim: new Date(),
    insumo: null
  };

  tiposMovimentacao = [
    {valor: null, nome: "Todos os tipos"},
    {valor: "entrada", nome: "Entrada"},
    {valor: "saida", nome: "Saída"},
    {valor: "devolucao", nome: "Devolução"},
    {valor: "reavaliacao", nome: "Reavaliação"}
  ];
  insumosMovimentacao: any[] = [];
  insumosEvolucao: any[] = [];
  movimentacoesCarregadas = false;
  inconsistenciasCarregadas = false;

  constructor(private estoqueService: EstoqueService) {}

  ngOnInit(): void {
    this.atualizeListasDeInsumos();
    this.carreguePosicao();
    this.carregueInconsistencias();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if(changes.insumos)
      this.atualizeListasDeInsumos();
  }

  selecionouRelatorio(evento: any): void {
    if(evento.index === 1 && !this.movimentacoesCarregadas)
      this.carregueMovimentacoes();
    if(evento.index === 3 && !this.inconsistenciasCarregadas)
      this.carregueInconsistencias();
  }

  async carreguePosicao(): Promise<void> {
    this.carregandoPosicao = true;
    this.erroPosicao = null;
    try {
      const resposta: any = await this.estoqueService.listeRelatorioPosicaoEstoque(
        this.monteFiltroPosicao(true)
      );
      this.resumoPosicao = resposta.resumo || {total: 0, pendenciasCusto: 0, valorTotal: null};
      this.posicao = {
        data: resposta.itens || [],
        total: Number(this.resumoPosicao.total || 0)
      };
    } catch(erro) {
      this.erroPosicao = this.mensagemDeErro(erro);
      this.posicao = {data: [], total: 0};
    } finally {
      this.carregandoPosicao = false;
    }
  }

  apliqueFiltroPosicao(): void {
    this.skipPosicao = 0;
    this.carreguePosicao();
  }

  mudouPaginaPosicao(evento: PageChangeEvent): void {
    this.skipPosicao = evento.skip;
    this.tamanhoPosicao = evento.take;
    this.carreguePosicao();
  }

  async carregueMovimentacoes(): Promise<void> {
    this.carregandoMovimentacoes = true;
    this.erroMovimentacoes = null;
    try {
      const resposta: any = await this.estoqueService.listeRelatorioMovimentacoesValorizadas(
        this.monteFiltroMovimentacoes(true)
      );
      this.resumoMovimentacoes = resposta.resumo || {total: 0, custoTotalMovimentado: null};
      this.movimentacoes = {
        data: resposta.itens || [],
        total: Number(this.resumoMovimentacoes.total || 0)
      };
      this.movimentacoesCarregadas = true;
    } catch(erro) {
      this.erroMovimentacoes = this.mensagemDeErro(erro);
      this.movimentacoes = {data: [], total: 0};
    } finally {
      this.carregandoMovimentacoes = false;
    }
  }

  apliqueFiltroMovimentacoes(): void {
    this.skipMovimentacoes = 0;
    this.carregueMovimentacoes();
  }

  mudouPaginaMovimentacoes(evento: PageChangeEvent): void {
    this.skipMovimentacoes = evento.skip;
    this.tamanhoMovimentacoes = evento.take;
    this.carregueMovimentacoes();
  }

  async carregueEvolucao(): Promise<void> {
    if(!this.filtroEvolucao.insumo || !this.filtroEvolucao.insumo.id){
      this.erroEvolucao = "Selecione um insumo para consultar a evolução do custo médio.";
      this.evolucao = [];
      this.evolucaoCategorias = [];
      this.evolucaoValores = [];
      return;
    }

    this.carregandoEvolucao = true;
    this.erroEvolucao = null;
    try {
      const resposta: any[] = await this.estoqueService.listeEvolucaoCustoMedio({
        i: 0,
        t: 100,
        iid: this.filtroEvolucao.insumo.id,
        di: this.formateData(this.filtroEvolucao.inicio),
        df: this.formateData(this.filtroEvolucao.fim)
      });
      this.evolucao = resposta || [];
      const ordemCronologica = this.evolucao.slice().sort((a: any, b: any) => {
        const diferenca = new Date(a.processadoEm).getTime() - new Date(b.processadoEm).getTime();
        return diferenca || Number(a.idMovimentacao) - Number(b.idMovimentacao);
      });
      this.evolucaoCategorias = ordemCronologica.map(item => moment(item.processadoEm).format("DD/MM/YY"));
      this.evolucaoValores = ordemCronologica.map(item => Number(item.custoMedioResultante));
    } catch(erro) {
      this.erroEvolucao = this.mensagemDeErro(erro);
      this.evolucao = [];
      this.evolucaoCategorias = [];
      this.evolucaoValores = [];
    } finally {
      this.carregandoEvolucao = false;
    }
  }

  abraEvolucao(item: any): void {
    this.filtroEvolucao.insumo = this.insumos.find(insumo => Number(insumo.id) === Number(item.idInsumo)) || {
      id: item.idInsumo,
      codigo: item.codigo,
      nome: item.insumo
    };
    this.abasRelatorios.selectTab(2);
    this.carregueEvolucao();
  }

  async carregueInconsistencias(): Promise<void> {
    this.carregandoInconsistencias = true;
    this.erroInconsistencias = null;
    try {
      this.inconsistencias = await this.estoqueService.listeInconsistenciasFinanceiras() || [];
      this.inconsistenciasCarregadas = true;
    } catch(erro) {
      this.erroInconsistencias = this.mensagemDeErro(erro);
      this.inconsistencias = [];
    } finally {
      this.carregandoInconsistencias = false;
    }
  }

  filtreInsumosMovimentacao(texto: string): void {
    this.insumosMovimentacao = this.filtreInsumos(texto);
  }

  filtreInsumosEvolucao(texto: string): void {
    this.insumosEvolucao = this.filtreInsumos(texto);
  }

  obtenhaUrlCsvPosicao(): string {
    return this.monteUrlCsv("/estoque/custos/relatorios/posicao.csv", this.monteFiltroPosicao(false));
  }

  obtenhaUrlCsvMovimentacoes(): string {
    return this.monteUrlCsv(
      "/estoque/custos/relatorios/movimentacoes.csv",
      this.monteFiltroMovimentacoes(false)
    );
  }

  tituloInconsistencia(tipo: string): string {
    if(tipo === "SALDO_INVALIDO") return "Saldo inválido";
    if(tipo === "CUSTO_DESCONHECIDO") return "Custo desconhecido";
    if(tipo === "DIVERGENCIA_FINANCEIRA") return "Divergência financeira";
    return tipo || "Inconsistência";
  }

  classeInconsistencia(tipo: string): string {
    return tipo === "CUSTO_DESCONHECIDO" ? "alert-warning" : "alert-danger";
  }

  private atualizeListasDeInsumos(): void {
    this.insumosMovimentacao = (this.insumos || []).slice();
    this.insumosEvolucao = (this.insumos || []).slice();
  }

  private filtreInsumos(texto: string): any[] {
    const termo = String(texto || "").trim().toLocaleLowerCase();
    if(!termo) return (this.insumos || []).slice();
    return (this.insumos || []).filter(insumo => {
      return String(insumo.nome || "").toLocaleLowerCase().indexOf(termo) >= 0 ||
        String(insumo.codigo || "").toLocaleLowerCase().indexOf(termo) >= 0;
    });
  }

  private monteFiltroPosicao(paginado: boolean): any {
    const filtro: any = {};
    if(paginado){
      filtro.i = this.skipPosicao;
      filtro.t = this.tamanhoPosicao;
    }
    if(this.filtroPosicao.texto && String(this.filtroPosicao.texto).trim())
      filtro.termo = String(this.filtroPosicao.texto).trim();
    if(this.filtroPosicao.grupo && this.filtroPosicao.grupo.id)
      filtro.gid = this.filtroPosicao.grupo.id;
    return filtro;
  }

  private monteFiltroMovimentacoes(paginado: boolean): any {
    const filtro: any = {};
    if(paginado){
      filtro.i = this.skipMovimentacoes;
      filtro.t = this.tamanhoMovimentacoes;
    }
    if(this.filtroMovimentacoes.inicio)
      filtro.di = this.formateData(this.filtroMovimentacoes.inicio);
    if(this.filtroMovimentacoes.fim)
      filtro.df = this.formateData(this.filtroMovimentacoes.fim);
    if(this.filtroMovimentacoes.grupo && this.filtroMovimentacoes.grupo.id)
      filtro.gid = this.filtroMovimentacoes.grupo.id;
    if(this.filtroMovimentacoes.insumo && this.filtroMovimentacoes.insumo.id)
      filtro.iid = this.filtroMovimentacoes.insumo.id;
    if(this.filtroMovimentacoes.tipo && this.filtroMovimentacoes.tipo.valor)
      filtro.tipo = this.filtroMovimentacoes.tipo.valor;
    return filtro;
  }

  private formateData(data: Date): string {
    return data ? moment(data).format("YYYYMMDD") : null;
  }

  private monteUrlCsv(endpoint: string, filtro: any): string {
    const parametros = Object.keys(filtro)
      .filter(chave => filtro[chave] !== null && filtro[chave] !== undefined && filtro[chave] !== "")
      .map(chave => `${encodeURIComponent(chave)}=${encodeURIComponent(filtro[chave])}`)
      .join("&");
    return parametros ? `${endpoint}?${parametros}` : endpoint;
  }

  private mensagemDeErro(erro: any): string {
    if(typeof erro === "string") return erro;
    return erro && erro.message ? erro.message : "Não foi possível carregar o relatório.";
  }
}
